/**
 * Ask Salah write APIs — subscriptions and access tokens via sal-core; sessions stay on sal-api.
 */
import { post } from '../index';
import { authSubsPost } from '../authSubs';
import {
  createAskSalahCoreOrder,
  startAskSalahInstantTrial,
} from './coreSubscription';
import type {
  AskSalahTokenResponse,
  AskSalahSessionType,
  CreateAskSalahOrderRequest,
  AskSalahOrderResponse,
} from 'src/types/askSalah';

/**
 * Start Ask Salah free trial (instant activation on sal-core)
 */
export const startAskSalahFreeTrial = (
  locale?: string,
): Promise<{ success: boolean; message: string }> =>
  startAskSalahInstantTrial(locale);

/**
 * @deprecated Use createAskSalahOrder with quotationId from sal-core quotations
 */
export const subscribeToAskSalah = (): never => {
  throw new Error('subscribeToAskSalah is deprecated; use createAskSalahOrder');
};

/**
 * Create Ask Salah order on sal-core (product=ask-salah)
 */
export const createAskSalahOrder = (
  data: CreateAskSalahOrderRequest,
  locale?: string,
): Promise<AskSalahOrderResponse> => {
  if (data.planType === 'trial') {
    return startAskSalahInstantTrial(locale) as unknown as Promise<AskSalahOrderResponse>;
  }
  if (!data.quotationId) {
    return Promise.reject(new Error('quotationId is required for paid Ask Salah plans'));
  }
  return createAskSalahCoreOrder(
    {
      quotationId: data.quotationId,
      paymentMethod: data.paymentMethod,
      proveUrl: data.proveUrl,
      gateway: data.gateway,
    },
    locale,
  );
};

/**
 * Generate one-time authentication token for external Ask Salah site (sal-core)
 */
export const generateAskSalahToken = (
  locale?: string,
): Promise<AskSalahTokenResponse> =>
  authSubsPost<AskSalahTokenResponse>('/ask-salah/generate-token', {}, locale);

/**
 * Record a session usage (sal-api proxies to sal-core metadata)
 */
export const recordAskSalahSession = (
  sessionType: AskSalahSessionType,
): Promise<{ success: boolean; message: string }> =>
  post('/ask-salah/session', { sessionType }) as unknown as Promise<{ success: boolean; message: string }>;
